import React from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet';

const SEOHelmet = ({ 
  title, 
  description, 
  canonicalUrl, 
  imageUrl,
  schemaType = 'WebPage',
  keywords,
  modelData = null,
  photoshootData = null,
  videoData = null,
  breadcrumbs = []
}) => {
  // Ultra-safe string conversion - absolutely no Symbol values allowed
  const ultraSafe = (value) => {
    if (value === null || value === undefined || typeof value === 'symbol') {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    try {
      const str = String(value);
      if (str === '[object Symbol]') {
        return '';
      }
      return str;
    } catch (e) {
      console.warn('Failed to convert value to string:', value);
      return '';
    }
  };

  // Safe array processing
  const safeArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.filter(item => typeof item !== 'symbol' && item != null)
              .map(item => ultraSafe(item))
              .filter(item => item !== '');
  };

  const siteTitle = 'Aegyo Models';
  const safeTitle = ultraSafe(title);
  const fullTitle = safeTitle ? `${safeTitle} | ${siteTitle}` : siteTitle;
  const safeDescription = ultraSafe(description) || 'Korean Model Gallery featuring professional fashion photography';
  const safeCanonicalUrl = ultraSafe(canonicalUrl) || 'https://aegyomodels.com';
  const safeImageUrl = ultraSafe(imageUrl) || 'https://aegyomodels.com/og-image.jpg';
  const ogType = (schemaType === 'Article') ? 'article' : 'website';
  
  // Safe keywords processing
  let safeKeywords = '';
  if (typeof keywords === 'string') {
    safeKeywords = ultraSafe(keywords);
  } else if (Array.isArray(keywords)) {
    safeKeywords = safeArray(keywords).join(', ');
  }
  if (!safeKeywords) {
    safeKeywords = 'Korean models, fashion, photography';
  }

  // Generate schema markup based on schema type
  const generateSchemaMarkup = () => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": ultraSafe(schemaType) || "WebPage",
      "name": fullTitle,
      "description": safeDescription,
      "url": safeCanonicalUrl,
      "image": safeImageUrl,
      "publisher": {
        "@type": "Organization",
        "name": "Aegyo Models",
        "url": "https://aegyomodels.com"
      }
    };

    // Add breadcrumb schema if breadcrumbs exist
    if (breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      try {
        const safeBreadcrumbs = breadcrumbs
          .filter(item => item && typeof item === 'object' && typeof item.name === 'string')
          .map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": ultraSafe(item.name),
            "item": ultraSafe(item.url)
          }));

        if (safeBreadcrumbs.length > 0) {
          baseSchema.breadcrumb = {
            "@type": "BreadcrumbList",
            "itemListElement": safeBreadcrumbs
          };
        }
      } catch (e) {
        console.warn('Error processing breadcrumbs:', e);
      }
    }

    // Add video schema if videoData exists
    if (videoData && typeof videoData === 'object' && videoData !== null) {
      try {
        // Extra safety checks for videoData properties
        const getThumbnailUrl = () => {
          try {
            return videoData.thumbnailUrl || videoData.thumbnail || '';
          } catch (e) {
            console.warn('Error getting thumbnail URL:', e);
            return '';
          }
        };

        const getContentUrl = () => {
          try {
            return videoData.contentUrl || videoData.url || '';
          } catch (e) {
            console.warn('Error getting content URL:', e);
            return '';
          }
        };

        const safeVideoData = {
          "@type": "VideoObject",
          "name": ultraSafe(videoData.title) || fullTitle,
          "description": ultraSafe(videoData.description) || safeDescription,
          "thumbnailUrl": ultraSafe(getThumbnailUrl()),
          "uploadDate": ultraSafe(videoData.uploadDate),
          "duration": ultraSafe(videoData.duration),
          "contentUrl": ultraSafe(getContentUrl()),
          "embedUrl": ultraSafe(videoData.embedUrl),
          "publisher": {
            "@type": "Organization",
            "name": "Aegyo Models",
            "url": "https://aegyomodels.com"
          }
        };

        // Add view count if available
        if (videoData.viewCount && !isNaN(videoData.viewCount)) {
          safeVideoData.interactionStatistic = {
            "@type": "InteractionCounter",
            "interactionType": { "@type": "WatchAction" },
            "userInteractionCount": parseInt(videoData.viewCount, 10)
          };
        }

        // Add tags if available
        if (videoData.tags && Array.isArray(videoData.tags)) {
          const safeTags = safeArray(videoData.tags);
          if (safeTags.length > 0) {
            safeVideoData.keywords = safeTags.join(', ');
          }
        }

        baseSchema.video = safeVideoData;
      } catch (e) {
        console.warn('Error processing video data:', e);
      }
    }

    // Enhance schema based on type
    if (schemaType === 'Article' && modelData) {
      try {
        baseSchema.author = {
          "@type": "Organization",
          "name": "Aegyo Models Team"
        };
        baseSchema.headline = fullTitle;
        baseSchema.datePublished = ultraSafe(modelData.publishedDate);
        baseSchema.dateModified = ultraSafe(modelData.updatedDate);
      } catch (e) {
        console.warn('Error processing article schema:', e);
      }
    }

    if (schemaType === 'ProfilePage' && modelData) {
      try {
        baseSchema.mainEntity = {
          "@type": "Person",
          "name": ultraSafe(modelData.name),
          "alternateName": ultraSafe(modelData.koreanName),
          "jobTitle": "Fashion Model",
          "nationality": "South Korean",
          "image": safeImageUrl
        };
      } catch (e) {
        console.warn('Error processing profile schema:', e);
      }
    }

    return baseSchema;
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={safeDescription} />
      <meta name="keywords" content={safeKeywords} />
      <link rel="canonical" href={safeCanonicalUrl} />
      
      {/* Basic SEO meta tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Aegyo Models" />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:url" content={safeCanonicalUrl} />
      <meta property="og:image" content={safeImageUrl} />
  <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Aegyo Models" />
      
      {/* Video-specific Open Graph tags */}
      {videoData && videoData.embedUrl && (
        <>
          <meta property="og:video" content={ultraSafe(videoData.embedUrl)} />
          <meta property="og:video:type" content="text/html" />
          <meta property="og:video:width" content="1280" />
          <meta property="og:video:height" content="720" />
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={safeDescription} />
      <meta name="twitter:image" content={safeImageUrl} />
      
      {/* Video-specific Twitter tags */}
      {videoData && videoData.embedUrl && (
        <>
          <meta name="twitter:player" content={ultraSafe(videoData.embedUrl)} />
          <meta name="twitter:player:width" content="1280" />
          <meta name="twitter:player:height" content="720" />
        </>
      )}
      
      {/* Enhanced schema markup */}
      <script type="application/ld+json">
        {JSON.stringify(generateSchemaMarkup(), (key, value) => {
          // Remove any Symbol values during JSON stringification
          if (typeof value === 'symbol' || typeof value === 'function') {
            return undefined;
          }
          return value;
        })}
      </script>
    </Helmet>
  );
};

SEOHelmet.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonicalUrl: PropTypes.string,
  imageUrl: PropTypes.string,
  schemaType: PropTypes.string,
  keywords: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  modelData: PropTypes.object,
  photoshootData: PropTypes.object,
  videoData: PropTypes.object,
  breadcrumbs: PropTypes.array
};

export default SEOHelmet;
